"use client";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import React, { useEffect } from "react";

// ========================================
// UTILITIES
// ========================================

/**
 * Attende che l'API TCF 2.0 sia disponibile
 * Usa polling intelligente con timeout
 */
function waitForTCFAPI(): Promise<boolean> {
  return new Promise((resolve) => {
    // Se già disponibile, risolvi immediatamente
    if (typeof window !== "undefined" && (window as any).__tcfapi) {
      console.log("✅ TCF API già disponibile");
      resolve(true);
      return;
    }

    console.log("⏳ Attendo TCF API...");

    let attempts = 0;
    const maxAttempts = 50; // 50 * 100ms = 5 secondi max

    const checkInterval = setInterval(() => {
      attempts++;

      if ((window as any).__tcfapi) {
        clearInterval(checkInterval);
        console.log(`✅ TCF API disponibile dopo ${attempts * 100}ms`);
        resolve(true);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.warn("⚠️ TCF API non disponibile dopo 5 secondi - procedo senza");
        resolve(false);
      }
    }, 100);
  });
}

/**
 * Verifica il consenso TCF 2.0 (Ezoic/Gatekeeper)
 * DEVE essere chiamata DOPO che waitForTCFAPI() è completata
 *
 * IMPORTANTE: Usa addEventListener per aspettare che l'utente completi l'azione
 * invece di getTCData che ritorna lo stato corrente (potrebbe essere incompleto)
 */
function checkTCFConsent(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !(window as any).__tcfapi) {
      console.log("⚠️ TCF API non disponibile, assumo NO consenso");
      resolve(false);
      return;
    }

    let hasResolved = false;
    const DONE_STATUSES = new Set(["tcloaded", "useractioncomplete"]);
    const CMP_TIMEOUT_MS = 10000;

    const timeout = setTimeout(() => {
      if (!hasResolved) {
        hasResolved = true;
        console.log("⏱️ Timeout consenso TCF - assumo NO consenso");
        resolve(false);
      }
    }, CMP_TIMEOUT_MS);

    const handleTCFEvent = (tcData: any, success: boolean) => {
      if (!success || !tcData || hasResolved) return;

      console.log("🔍 DEBUG TCF eventStatus:", tcData.eventStatus);
      console.log("🔍 DEBUG TCF gdprApplies:", tcData.gdprApplies);
      console.log("🔍 DEBUG TCF cmpStatus:", tcData.cmpStatus);
      console.log("🔍 DEBUG TCF tcData:", JSON.stringify(tcData, null, 2));

      // Aspetta solo finché l'evento NON è definitivo
      if (!DONE_STATUSES.has(tcData.eventStatus)) {
        console.log(
          "⏳ EventStatus non definitivo, aspetto. (eventStatus:",
          tcData.eventStatus + ")"
        );
        return;
      }

      hasResolved = true;
      clearTimeout(timeout);

      // Caso 1: GDPR non applicabile in modo affidabile
      if (tcData.gdprApplies === false) {
        console.log("✅ GDPR non applicabile, consenso non richiesto");
        resolve(true);
        return;
      }

      // Caso 2: GDPR applicabile o non specificato -> calcola consenso analytics
      const hasConsent =
        tcData.purpose?.consents?.[1] === true ||
        tcData.purpose?.consents?.[5] === true ||
        tcData.purpose?.legitimateInterests?.[1] === true ||
        tcData.purpose?.legitimateInterests?.[5] === true;

      if (hasConsent) {
        console.log("✅ Consenso analytics CONFERMATO", {
          purpose1: tcData.purpose?.consents?.[1],
          purpose5: tcData.purpose?.consents?.[5],
          eventStatus: tcData.eventStatus,
        });
        resolve(true);
      } else {
        console.log("⚠️ Consenso analytics RIFIUTATO", {
          purpose1: tcData.purpose?.consents?.[1],
          purpose5: tcData.purpose?.consents?.[5],
          eventStatus: tcData.eventStatus,
        });
        resolve(false);
      }
    };

    // Registra listener sugli eventi TCF
    (window as any).__tcfapi("addEventListener", 2, handleTCFEvent);
  });
}

// ========================================
// POSTHOG INITIALIZATION
// ========================================

/**
 * Inizializza PostHog con la configurazione corretta basata sul consenso
 * CRITICO: Questa funzione deve essere chiamata UNA SOLA VOLTA
 * La persistence configuration non può essere modificata dopo init
 */
function initializePostHog(hasConsent: boolean) {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!key || !host) {
    console.warn("❌ PostHog non inizializzato: variabili d'ambiente mancanti", {
      hasKey: !!key,
      hasHost: !!host,
    });
    return;
  }

  // Verifica se già inizializzato
  if (posthog.__loaded) {
    console.warn("⚠️ PostHog già inizializzato, skip re-init");
    return;
  }

  console.log(`🚀 Inizializzo PostHog con consenso: ${hasConsent ? "SÌ" : "NO"}`);

  try {
    posthog.init(key, {
      api_host: host,

      // CONFIGURAZIONE PERSISTENCE - CRITICA!
      // Questa configurazione è IMMUTABILE dopo init
      persistence: hasConsent ? "localStorage+cookie" : "memory",
      disable_cookie: !hasConsent,

      // Person profiles
      person_profiles: hasConsent ? "identified_only" : "never",

      // Opt-out configuration
      opt_out_capturing_by_default: !hasConsent,

      // Capture configuration
      capture_pageview: false, // Gestiamo manualmente con PostHogPageView
      capture_pageleave: true,
      autocapture: false,

      // Features disabilitate
      disable_session_recording: true,
      disable_surveys: true,
      enable_heatmaps: false,

      // Callback quando PostHog è completamente caricato
      loaded: (ph) => {
        if (hasConsent) {
          ph.opt_in_capturing();
          console.log("✅ PostHog inizializzato con TRACKING ABILITATO");
        } else {
          ph.opt_out_capturing();
          console.log("✅ PostHog inizializzato con TRACKING DISABILITATO (no consenso)");
        }
      },
    });

    console.log("✅ PostHog init completato");
  } catch (error) {
    console.error("❌ Errore inizializzazione PostHog:", error);
  }
}

// ========================================
// CONSENT CHANGE LISTENER
// ========================================

/**
 * Setup listener per cambiamenti del consenso
 * Gestisce revoche e accettazioni post-init
 */
function setupConsentListener() {
  if (typeof window === "undefined" || !(window as any).__tcfapi) {
    console.log("⚠️ TCF API non disponibile, skip consent listener");
    return;
  }

  console.log("👂 Setup listener per cambiamenti consenso");

  (window as any).__tcfapi("addEventListener", 2, (tcData: any, success: boolean) => {
    if (!success || !tcData) return;

    // Ignora eventi prima che PostHog sia caricato
    if (!posthog.__loaded) {
      console.log("⏳ PostHog non ancora caricato, skip consent change");
      return;
    }

    // IMPORTANTE: Se GDPR non si applica, ignora eventi del listener
    // Il consenso non è richiesto, quindi non ha senso gestire cambiamenti
    if (tcData.gdprApplies === false) {
      console.log("ℹ️ GDPR non applicabile, skip consent change listener");
      return;
    }

    const hasConsent =
      tcData.purpose?.consents?.[1] === true ||
      tcData.purpose?.consents?.[5] === true ||
      tcData.purpose?.legitimateInterests?.[1] === true ||
      tcData.purpose?.legitimateInterests?.[5] === true;

    const wasOptedOut = posthog.has_opted_out_capturing();

    // CASO 1: Utente REVOCA consenso (aveva accettato, ora revoca)
    if (!hasConsent && !wasOptedOut) {
      console.log("⚠️ Consenso REVOCATO - disabilito tracking");
      posthog.opt_out_capturing();
      return;
    }

    // CASO 2: Utente ACCETTA consenso (aveva rifiutato, ora accetta)
    if (hasConsent && wasOptedOut) {
      console.log("✅ Consenso ACCETTATO dopo init");

      // ⚠️ NOTA: PostHog è stato inizializzato con persistence: "memory"
      // Chiamare opt_in_capturing() riattiva il tracking MA la persistence rimane in memory
      // L'utente verrà perso al prossimo refresh

      // SOLUZIONE: Opt-in per questa sessione (limitato ma funzionale)
      posthog.opt_in_capturing();
      console.log("✅ Tracking abilitato (persistence: memory - utente perso al refresh)");

      // OPZIONE 2: Forza reload per re-init con persistence corretta
      // Decommenta se vuoi re-init completo con cookies/localStorage:
      /*
      console.log("🔄 Ricarico pagina per re-init PostHog con persistence corretta");
      sessionStorage.setItem("posthog_consent_granted", "true");
      setTimeout(() => {
        window.location.reload();
      }, 500);
      */
    }
  });
}

// ========================================
// PROVIDER COMPONENT
// ========================================

export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const initPostHog = async () => {
      console.log("🔧 PHProvider: Inizio inizializzazione.");

      // STEP 1: Attendi che TCF API sia disponibile
      const tcfAvailable = await waitForTCFAPI();

      // STEP 2: Verifica consenso (ora sicuro che TCF è pronto o timeout)
      const hasConsent = tcfAvailable ? await checkTCFConsent() : false;

      // STEP 3: Inizializza PostHog con configurazione corretta
      initializePostHog(hasConsent);

      // STEP 4: Setup listener per cambiamenti futuri
      setupConsentListener();

      console.log("✅ PHProvider: Inizializzazione completata");
    };

    initPostHog();
  }, []); // esegue solo al mount

  // ⚠️ Importante: NON cambiare mai il wrapper del tree dopo il mount.
  // Renderizza sempre PostHogProvider, così i children non vengono smontati.
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
